<script>
import axios from 'axios';
import { mapState } from 'pinia';

import { useUserStore } from '../../../../store/useUserStore.js';

export default {
  name: 'CpSupportedActivity',
  components: {
    CpButton: require('../../button').default,
    CpButtonLink: require('../../button-link').default,
    CpGrid: require('../../grid').default,
    CpGridItem: require('../../grid-item').default,
    CpBox: require('../../box').default,
    CpBoxContent: require('../../box-content').default,
    CpPageContent: require('../../page-content.vue').default,
    CpRecipient: require('../../recipient').default,
    CpSliceToggle: require('../../inputs/SliceToggle').default,
    capitalizeWords: require('../../mixins/utils.js').default,
  },
  directives: {
    margin: require('../../directives/margins').margin,
    marginTop: require('../../directives/margins').marginTop,
    marginRight: require('../../directives/margins').marginRight,
    marginBottom: require('../../directives/margins').marginBottom,
    marginLeft: require('../../directives/margins').marginLeft,
  },
  props: {
    user: Object,
    recipients: Array,
  },
  data() {
    return {
      visibility: this.user.profile_visibility_state === 'visible',
      urlCodes: '',
      userName: this.capitalizeWords(this.user.first_name) ?? '',
    };
  },
  computed: {
    isViewingSelf() {
      return this.isUserLogin && this.userLoginData.id === Number(this.user.id);
    },
    hiddenRecipients() {
      const hiddenRecip = this.recipients.filter(recipient => {
        return recipient.visibility_state === 'hidden' || (!recipient.visibility_state && this.user.profile_visibility_state !== 'visible');
      });
      return hiddenRecip;
    },
    visibleRecipients() {
      return this.recipients.filter(recipient => {
        return recipient.visibility_state === 'visible' || (!recipient.visibility_state && this.user.profile_visibility_state === 'visible');
      });
    },
    ...mapState(useUserStore, ['isUserLogin', 'userLoginData']),
  },
  methods: {
    updateProfileVisibility() {
      const visibility_state = this.visibility ? 'visible' : 'hidden';
      this.$set(this.user, 'profile_visibility_state', visibility_state);
      axios.post(`/apiv2/user/update`, { profile_visibility_state: visibility_state }).then(() => {});

      axios.post(`/apiv2/user-support/update/all`, { visibility_state }).then(() => {
        this.recipients.map(recipient => (recipient.visibility_state = visibility_state));
      });
    },
  },
};
</script>

<template>
  <CpBox>
    <CpPageContent v-margin-top="1">
      <CpGrid justify="center">
        <CpGridItem span="12">
          <CpBox outlined v-margin-bottom="1">
            <CpBoxContent>
              <div v-if="isViewingSelf && recipients.length == 0" class="text-center">
                <p class="content-1" style="max-width: 620px; margin-left: auto; margin-right: auto">
                  When you support campaigns, they will show up here. Go discover and support campaigns raising money now.
                </p>
                <CpButtonLink lg primary-gradient href="{!!route('social-feed.index')!!}">Browse Political Feed</CpButtonLink>
              </div>
              <div v-else-if="!user.cSqGnC9jXj4rSgGR87K0anihD66GUQrk && isViewingSelf && recipients.length > 0" class="slice">
                <div class="sliceText">
                  <h5 class="subheadline1 neutral6-color" v-margin="0">Would you like to share the campaign you have supported?</h5>
                  <p class="body1 neutral6-color toggle-text" v-margin="0">
                    Show your support and help the campaign grow (your donation amount will not be displayed)
                  </p>
                </div>
                <CpSliceToggle @input="updateProfileVisibility" v-model="visibility" :toggleText="visibility ? 'All public' : 'Private'" />
              </div>

              <div v-if="visibleRecipients.length == 0 && !isViewingSelf" class="text-center" v-margin="3">
                <h4 class="subheadline1 neutral6-color">There are no items on {{ userName }}'s public profile.</h4>
                <p class="body1 neutral6-color" style="max-width: 620px; margin-left: auto; margin-right: auto">
                  Check back soon to see who {{ userName }} is supporting!
                </p>
              </div>

              <CpGrid v-if="visibleRecipients.length > 0" stretch-content>
                <CpGridItem
                  :span="$root.isMobile ? 12 : 6"
                  v-for="recipient in visibleRecipients"
                  :key="recipient.recipient.type + recipient.recipient.id"
                >
                  <CpRecipient
                    :can-edit="isViewingSelf"
                    :recipient="recipient"
                    :default-visibility="user.profile_visibility_state"
                    :append-to-href="'?' + urlCodes"
                    @update="updateRecipient($event)"
                  >
                  </CpRecipient>
                </CpGridItem>
              </CpGrid>

              <div v-if="isViewingSelf && hiddenRecipients.length > 0" v-margin-top="visibleRecipients && 1">
                <p class="H3 neutral6-color text-center">These are only visible to you.</p>
                <p class="body1 neutral6-color text-center" v-margin-bottom="1">
                  You can add items to your public profile by clicking the icon in the top right corner of each card.
                </p>

                <CpGrid stretch-content>
                  <CpGridItem
                    :span="$root.isMobile ? 12 : 6"
                    v-for="recipient in hiddenRecipients"
                    :key="recipient.recipient.type + recipient.recipient.id"
                  >
                    <CpRecipient
                      :can-edit="isViewingSelf"
                      :recipient="recipient"
                      :default-visibility="user.profile_visibility_state"
                      :append-to-href="'?ref_code=profiles&source_code=' + user.slug"
                      @update="updateRecipient($event)"
                    >
                    </CpRecipient>
                  </CpGridItem>
                </CpGrid>
              </div>
            </CpBoxContent>
          </CpBox>
        </CpGridItem>
      </CpGrid>
    </CpPageContent>
  </CpBox>
</template>

<style scoped>
.slice {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--neutral2);
  margin-bottom: 20px;
  padding-bottom: 5px;
}

.sliceText {
  display: flex;
  flex-direction: column;
}

.checkbox-label:has(input:disabled) {
  cursor: default;
}

@media screen and (max-width: 450px) {
  .toggle-text {
    flex: 3;
  }
}
</style>
